import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../context/NotificationContext";

const Notifications = () => {
  const { notifications, unreadCount, getTypeConfig, getRelativeTime, markAsRead, markAllRead, removeNotification } = useNotifications();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const filteredList = filter === "unread" ? notifications.filter((n) => !n.isRead) : filter === "read" ? notifications.filter((n) => n.isRead) : notifications;

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClickOutside = useCallback((e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, handleClickOutside]);

  const handleNotificationClick = useCallback(
    async (notification) => {
      if (!notification.isRead) {
        await markAsRead(notification.id);
      }
      const cfg = getTypeConfig(notification.type);
      if (cfg.path) {
        navigate(cfg.path);
      }
      setIsOpen(false);
    },
    [markAsRead, getTypeConfig, navigate]
  );

  const handleMarkAllRead = useCallback(
    async (e) => {
      e.stopPropagation();
      await markAllRead();
    },
    [markAllRead]
  );

  const handleDelete = useCallback(
    async (e, id) => {
      e.stopPropagation();
      await removeNotification(id);
    },
    [removeNotification]
  );

  return (
    <li className="dropdown notification-list" ref={dropdownRef}>
      <a
        className="nav-link waves-effect waves-light"
        onClick={handleToggle}
        role="button"
        aria-haspopup="false"
        aria-expanded={isOpen}
        style={{ cursor: "pointer", position: "relative" }}
      >
        <i className="fe-bell noti-icon"></i>
        {unreadCount > 0 && (
          <span className="badge badge-danger rounded-circle noti-icon-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </a>

      {isOpen && (
        <div
          className="dropdown-menu dropdown-menu-right dropdown-lg show"
          style={{ position: "absolute", willChange: "transform", top: "100%", right: 0 }}
        >
          <div className="dropdown-item noti-title">
            <h5 className="m-0 d-flex align-items-center justify-content-between">
              <span>Notifications</span>
              <span className="d-flex align-items-center gap-2">
                {unreadCount > 0 && (
                  <small
                    className="text-primary"
                    style={{ cursor: "pointer", fontSize: "12px" }}
                    onClick={handleMarkAllRead}
                  >
                    Mark all read
                  </small>
                )}
              </span>
            </h5>
            <div className="d-flex gap-2 mt-2">
              {["all", "unread", "read"].map((f) => (
                <small
                  key={f}
                  className={`px-2 py-1 rounded ${filter === f ? "bg-primary text-white" : "bg-light text-muted"}`}
                  style={{ cursor: "pointer", fontSize: "11px", textTransform: "capitalize" }}
                  onClick={() => setFilter(f)}
                >
                  {f}
                </small>
              ))}
            </div>
          </div>

          <div className="slim-scroll noti-scroll" style={{ maxHeight: "360px" }}>
            {filteredList.length === 0 ? (
              <div className="dropdown-item text-center text-muted py-4" style={{ fontSize: "13px" }}>
                No notifications
              </div>
            ) : (
              filteredList.map((notification) => {
                const cfg = getTypeConfig(notification.type);
                return (
                  <a
                    key={notification.id}
                    className={`dropdown-item notify-item ${!notification.isRead ? "notify-unread" : ""}`}
                    onClick={() => handleNotificationClick(notification)}
                    style={{ cursor: "pointer", position: "relative" }}
                  >
                    {!notification.isRead && (
                      <span
                        className="notify-dot"
                        style={{
                          position: "absolute",
                          top: "14px",
                          left: "6px",
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          backgroundColor: cfg.color,
                        }}
                      />
                    )}
                    <div className="notify-icon" style={{ backgroundColor: cfg.bg }}>
                      <i className={cfg.icon} style={{ color: cfg.color }}></i>
                    </div>
                    <p className="notify-details">
                      {notification.title}
                      <small className="text-muted">{getRelativeTime(notification.createdAt)}</small>
                    </p>
                    <span
                      className="notify-delete"
                      onClick={(e) => handleDelete(e, notification.id)}
                      style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        fontSize: "12px",
                        color: "#9ca3af",
                        cursor: "pointer",
                        opacity: 0.5,
                      }}
                      title="Delete"
                    >
                      ✕
                    </span>
                  </a>
                );
              })
            )}
          </div>
        </div>
      )}
    </li>
  );
};

export default Notifications;
